import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PLANS, PlanSummary, CheckoutHeader, MemberStepper, money, effectivePricing } from './checkout/PlanData';

function OptionCard({ id, label, price, checked, type, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      data-testid={testId}
      className={`group relative flex items-center gap-4 w-full text-left rounded-md overflow-hidden border bg-zinc-950 transition-all ${checked ? 'border-red-500 ring-2 ring-red-600/40 shadow-[0_0_24px_rgba(239,43,43,0.25)]' : 'border-red-900/40 hover:border-red-600/60'}`}
    >
      <span className={`flex items-center justify-center w-14 self-stretch ${checked ? 'bg-red-700/25' : 'bg-black/40'}`}>
        <span className={`flex items-center justify-center w-5 h-5 rounded-full border-2 ${checked ? 'border-red-500 bg-red-600' : 'border-zinc-600 bg-black'}`}>
          {checked && (
            type === 'radio' ? (
              <span className="w-1.5 h-1.5 rounded-full bg-white" />
            ) : (
              <svg viewBox="0 0 12 12" className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2.5 6.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )
          )}
        </span>
      </span>
      <span className="flex-1 py-4 pr-4">
        <span className="block text-[13px] font-semibold text-white tracking-wide">{label}</span>
        <span className="block mt-1 text-sm text-zinc-400 tabular-nums">{money(price)}</span>
      </span>
    </button>
  );
}

export default function MembershipCheckout() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const plan = PLANS[planId];

  const [promo, setPromo] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [selectedPT, setSelectedPT] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState(new Set());
  const [showRenewal, setShowRenewal] = useState(false);
  const [members, setMembers] = useState(1);

  // === Pre-select Kid Care add-on when arriving from the homepage banner ===
  // Banner CTA passes `?addon=kid-care` so the cheapest available kid-care
  // tier (kid-1 = $15/mo) auto-ticks. The customer can swap to a higher tier
  // for more children, but we never silently up-sell them.
  useEffect(() => {
    if (!plan) return;
    const addonParam = searchParams.get('addon');
    if (addonParam === 'kid-care') {
      const cheapestKidCare = plan.addOns.find((a) => a.id.startsWith('kid-'));
      if (cheapestKidCare) {
        setSelectedAddOns((cur) => {
          if (cur.has(cheapestKidCare.id)) return cur;
          const next = new Set(cur);
          next.add(cheapestKidCare.id);
          return next;
        });
      }
    }
  }, [plan, searchParams]);

  // Restore native cursor on these pages — WebsiteFx (custom red cursor) is
  // only mounted on the homepage, so without this override the global
  // `body { cursor: none }` rule leaves the cursor invisible here.
  useEffect(() => {
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'auto';
    return () => { document.body.style.cursor = prev; };
  }, []);

  const ptItem = plan?.ptCatalog?.find((p) => p.id === selectedPT) || null;
  const addOnsTotal = useMemo(
    () => (plan?.addOns || []).filter((a) => selectedAddOns.has(a.id)).reduce((acc, a) => acc + a.price, 0),
    [selectedAddOns, plan],
  );
  const firstDueDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  }, []);

  if (!plan) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center bg-zinc-950 border border-red-900/40 rounded-md p-10 max-w-md">
          <h1 className="font-display text-2xl text-white mb-3">Plan not found</h1>
          <p className="text-zinc-400 mb-6 text-sm">The membership you're looking for doesn't exist.</p>
          <Link to="/#memberships" className="inline-block px-6 py-3 bg-red-600 hover:bg-red-500 text-white text-xs tracking-[0.25em] font-bold rounded">
            BACK TO PLANS
          </Link>
        </div>
      </div>
    );
  }

  const togglePT = (id) => setSelectedPT((cur) => (cur === id ? null : id));
  const toggleAddOn = (id) =>
    setSelectedAddOns((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const { signUpDues, monthlyDues } = effectivePricing(plan, members);
  const promoDiscount = promoApplied ? 10 : 0;
  const dueTodaySubtotal = (plan.enrollmentFee || 0) + signUpDues + (ptItem?.price || 0) + addOnsTotal;
  const dueTodayTotal = Math.max(0, dueTodaySubtotal - promoDiscount);
  const perPaymentTotal = monthlyDues + (monthlyDues > 0 ? addOnsTotal : 0);

  const handleApplyPromo = () => {
    if (!promo.trim()) {
      toast.error('Enter a promo code first');
      return;
    }
    setPromoApplied(true);
    toast.success(`Promo "${promo.toUpperCase()}" applied — $10.00 off today`);
  };

  const handleNext = () => {
    try {
      sessionStorage.setItem(
        `checkout:${planId}`,
        JSON.stringify({
          planId,
          planTitle: plan.title,
          members,
          pt: ptItem,
          addOns: Array.from(selectedAddOns),
          promo: promoApplied ? promo : null,
          promoApplied,
          promoDiscount,
          dueToday: dueTodayTotal,
          monthly: perPaymentTotal,
        }),
      );
    } catch (_) { /* sessionStorage unavailable */ }
    navigate(`/membership/${planId}/info`);
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans overflow-x-hidden max-w-full" data-testid="checkout-page">
      {/* Ambient red glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[60%] h-[40%] bg-red-700/10 blur-[140px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-[40%] h-[30%] bg-red-700/8 blur-[120px] rounded-full" />
      </div>

      <div className="relative">
        <CheckoutHeader />

        <main className="max-w-[1280px] mx-auto px-4 sm:px-6 py-8 sm:py-10 pb-32 overflow-x-hidden">
          <div className="grid lg:grid-cols-[360px_1fr] gap-8 items-start">
            {/* === LEFT: SUMMARY === */}
            <div className="space-y-5">
              <PlanSummary
                plan={plan}
                members={members}
                ptItem={ptItem}
                addOnsTotal={addOnsTotal}
                addOnsLabel={plan.monthlyDues > 0 ? 'Add-Ons (1st month)' : 'Add-Ons'}
                promo={promo}
                promoApplied={promoApplied}
                promoDiscount={promoDiscount}
                firstDueDate={firstDueDate}
                showRenewal={showRenewal}
                onToggleRenewal={() => setShowRenewal((s) => !s)}
                testIdPrefix="checkout"
              />

              {/* Promo code box */}
              <section className="bg-zinc-950 border border-red-900/40 rounded-md p-5">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label htmlFor="promo" className="block text-[10px] tracking-[0.25em] text-zinc-500 mb-1.5 font-bold">
                      PROMO CODE
                    </label>
                    <input
                      id="promo"
                      data-testid="checkout-promo-input"
                      type="text"
                      value={promo}
                      onChange={(e) => { setPromo(e.target.value); setPromoApplied(false); }}
                      className="w-full bg-transparent border-b border-zinc-700 focus:border-red-500 outline-none py-2 text-sm text-white"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    data-testid="checkout-promo-apply"
                    className="px-4 py-2 text-xs tracking-[0.2em] font-bold border border-red-700/60 text-white hover:bg-red-700/20 hover:border-red-500 rounded transition"
                  >
                    ADD CODE
                  </button>
                </div>
              </section>
            </div>

            {/* === RIGHT: OPTIONS === */}
            <section className="bg-zinc-950 border border-red-900/40 rounded-md overflow-hidden" data-testid="checkout-options">
              <h2 className="text-center text-[15px] font-bold text-white py-4 border-b border-red-900/40 tracking-wide">
                Select Options
              </h2>

              <div className="p-6 sm:p-8 pb-8 space-y-8">
                {plan.membersConfigurable && (
                  <MemberStepper value={members} onChange={setMembers} max={plan.maxMembers || 6} plan={plan} />
                )}

                {plan.showPurchaseItems && plan.ptCatalog.length > 0 && (
                  <div>
                    <h3 className="text-[11px] font-bold tracking-[0.3em] text-red-500 uppercase mb-4">Purchase Items</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {plan.ptCatalog.map((it) => (
                        <OptionCard
                          key={it.id}
                          id={it.id}
                          label={it.label}
                          price={it.price}
                          checked={selectedPT === it.id}
                          type="radio"
                          onToggle={togglePT}
                          testId={`pt-${it.id}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {plan.addOns.length > 0 && (
                  <div>
                    <h3 className="text-[11px] font-bold tracking-[0.3em] text-red-500 uppercase mb-4">Select Add Ons</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {plan.addOns.map((it) => (
                        <OptionCard
                          key={it.id}
                          id={it.id}
                          label={it.label}
                          price={it.price}
                          checked={selectedAddOns.has(it.id)}
                          type="check"
                          onToggle={toggleAddOn}
                          testId={`addon-${it.id}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* === HERO TOTAL — prominent confidence-builder mirror of the CTA === */}
                <div className="relative mt-2" data-testid="checkout-hero-total">
                  <div className="absolute inset-0 bg-red-600/15 blur-[60px] rounded-full pointer-events-none" />
                  <div className="relative bg-gradient-to-b from-red-950/40 to-black/70 border border-red-700/50 rounded-lg p-6 text-center shadow-[0_0_40px_rgba(239,43,43,0.25)]">
                    <div className="text-[10px] tracking-[0.4em] text-red-400 font-bold uppercase mb-2">
                      {plan.paidInFull ? 'One-Time · 1 Year' : 'Total Due Today'}
                    </div>
                    <div className="font-display text-5xl sm:text-6xl tabular-nums text-white leading-none drop-shadow-[0_0_20px_rgba(239,43,43,0.45)]">
                      {money(dueTodayTotal)}
                    </div>
                    {plan.monthlyDues > 0 && (
                      <div className="mt-3 text-xs tracking-[0.2em] text-zinc-400">
                        then <span className="text-white font-bold">{money(perPaymentTotal)}</span> / month
                      </div>
                    )}
                    {plan.paidInFull && (
                      <div className="mt-3 text-xs tracking-[0.2em] text-zinc-400">
                        No monthly billing · No auto-renewal
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Bottom sticky CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <button
          type="button"
          onClick={handleNext}
          data-testid="checkout-next"
          className="block w-full bg-red-600 hover:bg-red-500 text-white py-3.5 sm:py-5 font-bold transition shadow-[0_-8px_30px_rgba(239,43,43,0.35)]"
        >
          <span className="flex items-center justify-center gap-2.5 sm:gap-3">
            <span className="text-[11px] sm:text-base tracking-[0.14em] sm:tracking-[0.3em]">
              <span className="sm:hidden">NEXT · MEMBER INFO</span>
              <span className="hidden sm:inline">NEXT (MEMBER INFORMATION)</span>
            </span>
            <span
              data-testid="checkout-next-total"
              className="tabular-nums font-display text-[15px] sm:text-lg leading-none px-2 py-1 rounded bg-black/30 backdrop-blur-sm border border-white/15 whitespace-nowrap"
            >
              {money(dueTodayTotal)}
            </span>
            <span aria-hidden="true" className="text-base sm:text-lg leading-none">→</span>
          </span>
        </button>
      </div>
    </div>
  );
}
